import apiClient from "@/services/api-client";

// ── Types ──────────────────────────────────────────────────

export interface AlertRule {
  id: string;
  type: string;
  name: string;
  description?: string | null;
  enabled: boolean;
  severity: "info" | "warning" | "critical";
  threshold: number;
  comparisonOp: string;
  evaluationWindowMin: number;
  consecutiveBreaches: number;
  applyToAll: boolean;
  tenantIds?: string[] | null;
  planIds?: string[] | null;
  actions: unknown[];
  createdAt: string;
  updatedAt: string;
  lastEvaluatedAt?: string | null;
  _count?: { alertHistory: number };
}

export interface AlertHistoryEntry {
  id: string;
  ruleId: string;
  tenantId: string;
  severity: string;
  title: string;
  message: string;
  data?: Record<string, unknown> | null;
  status: "open" | "acknowledged" | "resolved" | "snoozed";
  acknowledgedBy?: string | null;
  acknowledgedAt?: string | null;
  resolvedBy?: string | null;
  resolvedAt?: string | null;
  snoozedUntil?: string | null;
  firedAt: string;
  rule?: { name: string; type: string };
  tenant?: { name: string; domain?: string };
}

export interface AlertHistoryResponse {
  alerts: AlertHistoryEntry[];
  total: number;
}

export interface AlertSummary {
  openAlerts: number;
  acknowledgedAlerts: number;
  criticalAlerts: number;
  alertsToday: number;
}

// ── Service ────────────────────────────────────────────────

const ALERTS_PREFIX = "/alerts";

export const alertService = {
  // ── Rules ──────────────────────────────────────────────

  getAllRules: async (): Promise<AlertRule[]> => {
    const response = await apiClient.get<AlertRule[]>(`${ALERTS_PREFIX}/rules`);
    return response.data;
  },

  getRuleById: async (id: string): Promise<AlertRule> => {
    const response = await apiClient.get<AlertRule>(
      `${ALERTS_PREFIX}/rules/${id}`,
    );
    return response.data;
  },

  createRule: async (data: Partial<AlertRule>): Promise<AlertRule> => {
    const response = await apiClient.post<AlertRule>(
      `${ALERTS_PREFIX}/rules`,
      data,
    );
    return response.data;
  },

  updateRule: async (
    id: string,
    data: Partial<AlertRule>,
  ): Promise<AlertRule> => {
    const response = await apiClient.patch<AlertRule>(
      `${ALERTS_PREFIX}/rules/${id}`,
      data,
    );
    return response.data;
  },

  deleteRule: async (id: string): Promise<void> => {
    await apiClient.delete(`${ALERTS_PREFIX}/rules/${id}`);
  },

  // ── History ──────────────────────────────────────────────

  getAlertHistory: async (params?: {
    tenantId?: string;
    status?: string;
    severity?: string;
    ruleId?: string;
    limit?: number;
    offset?: number;
  }): Promise<AlertHistoryResponse> => {
    const response = await apiClient.get<AlertHistoryResponse>(
      `${ALERTS_PREFIX}/history`,
      { params },
    );
    return response.data;
  },

  getAlertSummary: async (): Promise<AlertSummary> => {
    const response = await apiClient.get<AlertSummary>(
      `${ALERTS_PREFIX}/summary`,
    );
    return response.data;
  },

  // ── Actions ──────────────────────────────────────────────

  acknowledgeAlert: async (id: string): Promise<AlertHistoryEntry> => {
    const response = await apiClient.patch<AlertHistoryEntry>(
      `${ALERTS_PREFIX}/history/${id}/acknowledge`,
    );
    return response.data;
  },

  resolveAlert: async (id: string): Promise<AlertHistoryEntry> => {
    const response = await apiClient.patch<AlertHistoryEntry>(
      `${ALERTS_PREFIX}/history/${id}/resolve`,
    );
    return response.data;
  },

  snoozeAlert: async (
    id: string,
    until: string,
  ): Promise<AlertHistoryEntry> => {
    const response = await apiClient.patch<AlertHistoryEntry>(
      `${ALERTS_PREFIX}/history/${id}/snooze`,
      { until },
    );
    return response.data;
  },

  triggerEvaluation: async (): Promise<void> => {
    await apiClient.post(`${ALERTS_PREFIX}/evaluate`);
  },
};
